import * as React from 'react';
import { cardsOf } from './DashboardSection.tsx';
import { insets } from './layout.ts';

// A band of a dashboard screen: the sections that cover one scope, on a
// tint of their own under a small scope label, "Last 30 days", so the
// period's aggregates read apart from the selected game's sections under
// the game bar. The label carries the meaning; the tint only groups.
// (Moved from home-section's HomeBand, 2026-10-10; the same class names.)

export interface DashboardBandProps {
  /** The band's scope, shown at its top-left: "Last 30 days". */
  label: string;
  /** Its sections spaced as the cards' rows are (`BandParams.continuous`). */
  continuous?: boolean;
  children?: React.ReactNode;
}

/**
 * Keeps the label over the cards, as a section's header is: inset to where
 * the leftmost card of the band's sections starts (layout.ts), so on a wide
 * window, where the cards are capped and centered, it sits at their left
 * edge rather than the band's. Measured whenever the band or a section's
 * cards resize, or the sections change.
 */
function useLabelWithinCards(band: React.RefObject<HTMLElement>, body: React.RefObject<HTMLElement>) {
  React.useLayoutEffect(() => {
    const s = band.current;
    const b = body.current;
    if (!s || !b || typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const resize = new ResizeObserver(() => schedule());
    let watched: Element[] = [];
    const update = () => {
      frame = 0;
      const grids = Array.from(b.querySelectorAll('.home-section-body'));
      // Follow the sections' card grids as they come and go.
      for (const g of watched) if (!grids.includes(g)) resize.unobserve(g);
      for (const g of grids) if (!watched.includes(g)) resize.observe(g);
      watched = grids;
      const st = getComputedStyle(s);
      const r = s.getBoundingClientRect();
      const start = r.left + s.clientLeft + parseFloat(st.paddingLeft);
      const end = r.left + s.clientLeft + s.clientWidth - parseFloat(st.paddingRight);
      const cards = grids.flatMap((g) => cardsOf(g)).map((c) => c.getBoundingClientRect());
      s.style.setProperty('--home-band-inset-start', `${insets({ start, end }, cards).start}px`);
    };
    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }
    resize.observe(s);
    const mutation = new MutationObserver(schedule);
    mutation.observe(b, { childList: true, subtree: true });
    update();
    return () => {
      resize.disconnect();
      mutation.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [band, body]);
}

export const DashboardBand: React.FC<DashboardBandProps> = ({ label, continuous, children }) => {
  const band = React.useRef<HTMLElement>(null);
  const body = React.useRef<HTMLDivElement>(null);
  useLabelWithinCards(band, body);
  return (
    <section ref={band} className={`home-band${continuous ? ' home-band-continuous' : ''}`} aria-label={label}>
      <p className="home-band-label">{label}</p>
      <div ref={body} className="home-band-body">{children}</div>
    </section>
  );
};
